import { marketApi, Site } from '@envato/foundation-types'
import * as classnames from 'classnames'
import * as React from 'react'

import { reportProductsImpressions } from '../../../../common/analytics/resultAnalytics'
import { isSearchResultMatchesEqual } from '../../../../common/utils/item'
import { getCurrentSiteDomain } from '../../../../common/utils/sites'
import { IItem } from '../../../../typings/searchResults'
import { ISeoItem } from '../../../../typings/seoMetadata'
import Button from '../../../components/button'
import Dropdown from '../../../components/dropdown'
import ItemPreviewer from '../../../components/itemPreviewer'
import { INewestItemsData } from '../../../containers/homePageContainer/createHomePageContainer'
import SearchLink from '../../../containers/searchLinkContainer'
import ItemThumbnailCarousel from '../itemThumbnailCarousel'
import SectionDescription from '../sectionDescription'

import * as styles from './newestItemSection.module.css'

interface IProps {
  categories?: marketApi.CategoriesResult
  name: string
  currentSite: Site
  metadata: ISeoItem
  fetchNewestItemsByCategory: (category: string) => any
  newestItemsData: INewestItemsData
  videoMuted: boolean
  nonSquare?: boolean
  hideCategories?: boolean
  muteVideo: () => any
  reportItemClick: (index: number, item: IItem, listName: string) => any
}

interface ICategoryOption {
  label: string
  value: string
}

class NewestItemSection extends React.Component<IProps> {
  public componentDidMount() {
    const { newestItemsData, currentSite } = this.props
    reportProductsImpressions({
      currentSite,
      itemRankOffset: 1,
      items: newestItemsData.items,
      page: 'Homepage',
      section: 'New items'
    })
  }

  public componentDidUpdate(nextProps: IProps) {
    const { newestItemsData, currentSite } = this.props
    if (
      !isSearchResultMatchesEqual(
        nextProps.newestItemsData.items,
        newestItemsData.items
      )
    ) {
      reportProductsImpressions({
        currentSite,
        itemRankOffset: 1,
        items: nextProps.newestItemsData.items,
        page: 'Homepage',
        section: 'New items'
      })
    }
  }

  public render() {
    const {
      name,
      currentSite,
      newestItemsData,
      categories,
      metadata,
      hideCategories,
      fetchNewestItemsByCategory,
      videoMuted,
      muteVideo,
      nonSquare,
      reportItemClick
    } = this.props

    const categoriesOptions = categories
      ? [
          {
            name: 'All categories',
            path: 'all'
          },
          ...categories
        ].map(category => {
          return {
            label: category.name,
            value: category.path
          }
        })
      : []

    // This is mainly for Photodune
    const carouselItemWidth = nonSquare ? 122 : 'auto'
    const shouldShowCategories = !hideCategories && categories

    return (
      <div className={styles.section}>
        <div>
          <h3 className={styles.heading}>{metadata.main_heading}</h3>
        </div>
        {shouldShowCategories && this.renderCategoriesList(categoriesOptions)}
        {shouldShowCategories && (
          <div className={styles.categoriesListMobile}>
            <Dropdown
              aria-label="Category"
              className={styles.select}
              options={categoriesOptions}
              onChange={event => {
                fetchNewestItemsByCategory(event.target.value)
              }}
              value={
                newestItemsData.itemsPendingCategory ||
                newestItemsData.itemsCategory
              }
            />
          </div>
        )}
        <div data-test-selector="newestItemSectionList">
          <ItemThumbnailCarousel itemWidth={carouselItemWidth}>
            {newestItemsData.items.map((item, index) => (
              <ItemPreviewer
                item={item}
                key={item.id}
                videoMuted={videoMuted}
                muteVideo={muteVideo}
                nonSquare={nonSquare}
                lazyLoad={true}
                onClick={() => reportItemClick(index, item, name)}
              />
            ))}
          </ItemThumbnailCarousel>
        </div>
        <SectionDescription metadata={metadata} HTag="h4" hideOnMobile>
          <Button
            large
            fixedWidth
            renderAs={SearchLink}
            locationParams={{ sort: 'date' }}
            data-test-selector="newestItemSectionButton"
            onClick={() => window.scrollTo(0, 0)}
          >
            All New Items
          </Button>
          <Button
            large
            fixedWidth
            href={`//${getCurrentSiteDomain(currentSite)}/page/top_sellers`}
            data-test-selector="newestItemSectionButton"
          >
            Popular Files
          </Button>
          {shouldShowCategories && (
            <Button
              large
              fixedWidth
              href={`//${getCurrentSiteDomain(currentSite)}/category`}
              data-test-selector="newestItemSectionButton"
            >
              Browse Categories
            </Button>
          )}
        </SectionDescription>
        <div
          className={
            newestItemsData.itemsPendingCategory &&
            !newestItemsData.loadingCachedCategory
              ? styles.loading
              : ''
          }
        />
      </div>
    )
  }

  private renderCategoriesList(options: ICategoryOption[]) {
    const { newestItemsData, fetchNewestItemsByCategory } = this.props

    return (
      <div className={styles.categoriesListDesktop}>
        {options.map(category => {
          const buttonSelected =
            newestItemsData.itemsPendingCategory === category.value ||
            (!newestItemsData.itemsPendingCategory &&
              newestItemsData.itemsCategory === category.value)
          const categoryClassName = classnames(styles.categoryLink, {
            [styles.activeCategoryLink]: buttonSelected
          })
          return (
            <button
              data-test-selector="newestItemSectionCategory"
              className={categoryClassName}
              key={category.value}
              onClick={() => {
                fetchNewestItemsByCategory(category.value)
              }}
            >
              {category.label}
            </button>
          )
        })}
      </div>
    )
  }
}

export default NewestItemSection
